import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiSettings } from '../config/api-settings';
import { ApiKeysService } from './api-keys.service';

import { RagResult } from '../../pages/playground/models/rag-result.model';
import { AnswerResult } from '../../pages/playground/models/answer-result.model';
import { SearchResult } from '../../pages/playground/models/search-result.model';
import { ImageResult } from '../../pages/playground/models/image-result.model';
import { PagesResult } from '../../pages/playground/models/pages-result.model';
import { LegalResult } from '../../pages/playground/models/legal-result.model';

/** Everything about one run besides the parsed result: what the response
 *  panel's status line, Headers and JSON tabs show. */
export interface PlaygroundRunInfo {
  status: number;
  headers: Record<string, string>;
  messages: string[];
  /** Which API key the run was billed to, e.g. "cks_live_43fE". */
  apiKeyPrefix: string | null;
  /** The exact request as a cURL command, as the backend ran it. */
  curl: string | null;
  /** `body.data` exactly as the API returned it, before any model mapping. */
  raw: unknown;
}

export interface PlaygroundRunResult<T> {
  result: T;
  latencyMs: number;
  /** Real credits consumed, as reported by the backend for this call not an estimate. */
  credits: number;
  run: PlaygroundRunInfo;
}

/**
 * The real `POST /playground/execute` response envelope an execution
 * record, not a bare result: the model data lives at `body.data`, sitting
 * alongside the real `latency_ms` / `credits` this call actually cost and
 * the query-level `body.success` flag (a 200 here doesn't by itself mean
 * the query succeeded that's `body.success`).
 */
interface PlaygroundExecuteResponse<T> {
  endpoint: string;
  status: number;
  latency_ms: number;
  credits: number;
  headers: Record<string, string>;
  body: {
    data: T;
    messages: string[];
    success: boolean;
  };
  curl: string;
  api_key_prefix: string;
}

/** Named args for `run()` an object on purpose, not positional params: `endpoint`
 *  and `apiId` are both plain strings, so a future reorder of positional args
 *  wouldn't even be caught by TypeScript. Every field documented here instead
 *  of via @param, since that's what actually stays attached to the field. */
interface RunOptions<TRaw, TModel> {
  /** Which backend endpoint to run sent as `endpoint` in the request body (e.g. 'rag', 'answer'). */
  endpoint: string;
  /** The endpoint-specific query parameters (question, filters, ...) sent as `params` in the request body. */
  params: object;
  /** Constructs the typed result model from the raw JSON response. */
  ModelCtor: new (data: TRaw) => TModel;
  /** The API key to run as. Pages with their own key selector (Rag, so far) pass the
   *  user's choice; pages without one yet omit it, and resolveApiId() falls back to
   *  the org's first active key so every playground page keeps working against the
   *  real `POST /playground/execute` contract in the meantime. */
  apiId?: string;
}

/** Every playground page posts through this one backend endpoint. */
const EXECUTE_URL = 'playground/execute';

@Injectable({ providedIn: 'root' })
export class PlaygroundService {
  constructor(
    private http: HttpClient,
    private apiKeysService: ApiKeysService,
  ) {}

  private run<TRaw, TModel>(options: RunOptions<TRaw, TModel>): Observable<PlaygroundRunResult<TModel>> {
    const { endpoint, params, ModelCtor, apiId } = options;

    return this.resolveApiId(apiId).pipe(
      switchMap(api_id =>
        this.http.post<PlaygroundExecuteResponse<TRaw>>(`${ApiSettings.fullPath}${EXECUTE_URL}`, {
          endpoint,
          params,
          api_id,
        }),
      ),
      map(res => {
        // A 200 HTTP status only means the envelope arrived the query itself
        // succeeded or failed per body.success, independent of transport status.
        if (!res.body.success) {
          throw new Error(res.body.messages?.join(', ') || 'The query did not succeed.');
        }
        return {
          result: new ModelCtor(res.body.data),
          latencyMs: res.latency_ms,
          credits: res.credits,
          run: {
            status: res.status,
            headers: res.headers ?? {},
            messages: res.body.messages ?? [],
            apiKeyPrefix: res.api_key_prefix ?? null,
            curl: res.curl ?? null,
            raw: res.body.data,
          },
        };
      }),
      catchError(err => {
        console.error(err);
        return throwError(err);
      }),
    );
  }

  private resolveApiId(apiId?: string): Observable<string | null> {
    if (apiId) return of(apiId);
    return this.apiKeysService.getActiveApiKeys().pipe(
      map(keys => keys[0]?.id ?? null),
    );
  }

  runRag(payload: object, apiId?: string): Observable<PlaygroundRunResult<RagResult>> {
    return this.run({ endpoint: 'rag', params: payload, ModelCtor: RagResult, apiId });
  }

  runAnswer(payload: object, apiId?: string): Observable<PlaygroundRunResult<AnswerResult>> {
    return this.run({ endpoint: 'answer', params: payload, ModelCtor: AnswerResult, apiId });
  }

  runSearch(payload: object, apiId?: string): Observable<PlaygroundRunResult<SearchResult>> {
    return this.run({ endpoint: 'search', params: payload, ModelCtor: SearchResult, apiId });
  }

  /** /similar answers in /search's shape, so it maps to SearchResult. */
  runSimilar(payload: object, apiId?: string): Observable<PlaygroundRunResult<SearchResult>> {
    return this.run({ endpoint: 'similar', params: payload, ModelCtor: SearchResult, apiId });
  }

  runImage(payload: object, apiId?: string): Observable<PlaygroundRunResult<ImageResult>> {
    return this.run({ endpoint: 'image', params: payload, ModelCtor: ImageResult, apiId });
  }

  runPages(payload: object, apiId?: string): Observable<PlaygroundRunResult<PagesResult>> {
    return this.run({ endpoint: 'pages', params: payload, ModelCtor: PagesResult, apiId });
  }

  runLegal(payload: object, apiId?: string): Observable<PlaygroundRunResult<LegalResult>> {
    return this.run({ endpoint: 'legal', params: payload, ModelCtor: LegalResult, apiId });
  }
}
